import React from 'react';
import { 
  Trophy, TrendingUp, TrendingDown, Timer, Activity, Ruler, Target, Zap, ChevronRight
} from 'lucide-react';

interface HistoryPoint {
  metric_date: string;
  metric_value: number;
}

interface MetricProps {
  metricKey: string;
  displayName: string;
  currentValue: number;
  previousValue: number | null;
  unit: string | null;
  improvementDirection: 'increase' | 'decrease';
  history: HistoryPoint[];
  theme?: 'light' | 'dark';
}

// --- Specialized Internal Components ---

const HistoricalProgress: React.FC<{
  history: HistoryPoint[];
  improvementDirection: 'increase' | 'decrease';
  theme: 'light' | 'dark';
  unit: string | null;
}> = ({ history, improvementDirection, theme, unit }) => {
  // Take last 4 points, ensure sorted by date ascending
  const pts = [...history]
    .sort((a, b) => new Date(a.metric_date).getTime() - new Date(b.metric_date).getTime())
    .slice(-4);

  return (
    <div className="flex items-center gap-1.5 mt-4 overflow-hidden">
      {pts.map((p, i) => {
        const isLatest = i === pts.length - 1;
        const prev = i > 0 ? pts[i - 1] : null;
        
        // Determine status color
        let colorClass = theme === 'dark' ? 'bg-zinc-800 text-zinc-500' : 'bg-gray-100 text-gray-400';
        if (prev) {
          const diff = p.metric_value - prev.metric_value;
          const improved = improvementDirection === 'increase' ? diff > 0 : diff < 0;
          if (diff === 0) {
            colorClass = theme === 'dark' ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-200 text-gray-500';
          } else if (improved) {
            colorClass = 'bg-[#AAD816]/20 text-[#AAD816] border-[#AAD816]/10';
            if (isLatest) colorClass = 'bg-[#AAD816] text-black';
          } else {
            colorClass = 'bg-red-500/10 text-red-500 border-red-500/10';
            if (isLatest) colorClass = 'bg-red-500 text-white';
          }
        } else if (isLatest) {
          colorClass = theme === 'dark' ? 'bg-zinc-700 text-white' : 'bg-gray-900 text-white';
        }

        const opacity = 0.3 + (i * 0.23); // Fade from 0.3 to 1.0

        return (
          <React.Fragment key={p.metric_date}>
            {i > 0 && <ChevronRight size={10} className={`${theme === 'dark' ? 'text-zinc-800' : 'text-gray-200'}`} style={{ opacity }} />}
            <div 
              className={`flex-1 min-w-0 px-1 py-2 rounded-lg border flex flex-col items-center justify-center transition-all ${colorClass}`}
              style={{ opacity: isLatest ? 1 : opacity }}
            >
              <span className={`text-[10px] font-black italic tracking-tighter truncate w-full text-center ${isLatest ? 'scale-110' : ''}`}>
                {p.metric_value}<span className="text-[7px] ml-0.5 not-italic opacity-70">{unit}</span>
              </span>
              <span className={`text-[6px] font-bold uppercase tracking-widest mt-0.5 ${isLatest ? 'opacity-80' : 'opacity-40'}`}>
                {new Date(p.metric_date).toLocaleDateString('en-US', { month: 'short' })}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// 1. Body Composition (Updated to remove LineChart)
export const BodyMetricCard: React.FC<MetricProps> = ({ 
  displayName, currentValue, previousValue, unit, improvementDirection, history, theme = 'dark' 
}) => {
  const isBetter = previousValue === null || 
    (improvementDirection === 'increase' ? currentValue > previousValue : currentValue < previousValue);
  
  const diff = previousValue !== null ? Math.abs(currentValue - previousValue).toFixed(1) : null;

  return (
    <div className={`${theme === 'dark' ? 'bg-[#111] border-zinc-800' : 'bg-white border-gray-100 shadow-sm shadow-gray-200/50 hover:shadow-md'} border rounded-2xl p-5 hover:border-zinc-700 transition-all group`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <p className={`${theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} text-[10px] font-black uppercase tracking-widest`}>{displayName}</p>
          <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'} text-3xl font-black mt-1 italic tracking-tighter`}>
            {currentValue.toLocaleString()} <span className={`text-sm font-normal ${theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} not-italic`}>{unit}</span>
          </h3>
        </div>
        <div className={`p-2 rounded-xl ${isBetter ? 'bg-[#AAD816]/10 text-[#AAD816] font-bold' : theme === 'dark' ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-400'}`}>
          <Activity size={18} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        {diff && (
          <div className={`flex items-center gap-1 text-[11px] font-black uppercase ${isBetter ? 'text-[#AAD816]' : theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'}`}>
            {isBetter ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
            {diff} {unit}
          </div>
        )}
        <span className={`${theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} text-[10px] font-bold uppercase tracking-tighter`}>vs previous</span>
      </div>

      <HistoricalProgress history={history} improvementDirection={improvementDirection} theme={theme} unit={unit} />
    </div>
  );
};

// 2. Strength (Weighted Lifts)
export const StrengthMetricCard: React.FC<MetricProps> = ({ 
  displayName, currentValue, previousValue, unit, improvementDirection, history, theme = 'dark' 
}) => {
  const isPR = previousValue === null || currentValue > previousValue;
  
  return (
    <div className={`${theme === 'dark' ? 'bg-[#111] border-zinc-800' : 'bg-white border-gray-100 shadow-xl shadow-gray-200/50'} border rounded-2xl p-5 hover:border-[#aad816]/50 transition-all relative overflow-hidden group shadow-lg`}>
      <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-gradient-to-br from-[#aad816]/5' : 'bg-gradient-to-br from-[#aad816]/10'} via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity`} />
      
      <div className="flex justify-between items-start mb-4 relative z-10">
        <div className="max-w-[70%]">
          <div className="flex flex-col gap-1">
            <p className={`${theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} text-[10px] font-black uppercase tracking-widest leading-tight`}>{displayName}</p>
            {isPR && (
              <span className="inline-flex items-center bg-[#aad816] text-black text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-tighter w-fit shadow-lg shadow-[#aad816]/20">
                🏆 PR UNLOCKED
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-2 mt-2">
            <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'} text-4xl font-black italic tracking-tighter leading-none`}>
                {currentValue}
            </h3>
            <span className="text-sm font-bold text-[#aad816] uppercase italic">{unit}</span>
          </div>
        </div>
        <div className={`p-2.5 rounded-2xl transition-all ${isPR ? 'bg-[#aad816] text-black shadow-[0_0_25px_rgba(170,216,22,0.4)] scale-110' : theme === 'dark' ? 'bg-zinc-800 text-zinc-500' : 'bg-gray-100 text-gray-400'}`}>
          <Trophy size={22} />
        </div>
      </div>

      <HistoricalProgress history={history} improvementDirection={improvementDirection} theme={theme} unit={unit} />
    </div>
  );
};

// 3. Bodyweight Strength (Reps) - Standardized layout
export const RepMetricCard: React.FC<MetricProps> = (props) => {
  const { displayName, currentValue, previousValue, unit, improvementDirection, history, theme = 'dark' } = props;
  const isBetter = previousValue === null || currentValue > previousValue;
  const diff = previousValue !== null ? currentValue - previousValue : null;

  return (
    <div className={`${theme === 'dark' ? 'bg-[#111] border-zinc-800' : 'bg-white border-gray-100 shadow-sm shadow-gray-200/50 hover:shadow-md'} border rounded-2xl p-5 hover:border-zinc-700 transition-all group`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <p className={`${theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} text-[10px] font-black uppercase tracking-widest`}>{displayName}</p>
          <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'} text-3xl font-black mt-1 italic tracking-tighter`}>
            {currentValue} <span className={`text-sm font-normal ${theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} not-italic`}>{unit}</span>
          </h3>
        </div>
        <div className={`p-2 rounded-xl ${isBetter ? 'bg-[#AAD816]/10 text-[#AAD816] font-bold' : theme === 'dark' ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-400'}`}>
          <Zap size={18} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        {diff !== null && (
          <div className={`text-[11px] font-black italic ${isBetter ? 'text-[#AAD816]' : theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'}`}>
            {diff > 0 ? `+${diff}` : diff} {unit}
          </div>
        )}
        <span className={`${theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} text-[10px] font-bold uppercase tracking-tighter`}>Growth</span>
      </div>

      <HistoricalProgress history={history} improvementDirection={improvementDirection} theme={theme} unit={unit} />
    </div>
  );
};

// 4. Endurance (Duration Hold) - Standardized layout
export const DurationMetricCard: React.FC<MetricProps> = (props) => {
  const { displayName, currentValue, previousValue, unit, improvementDirection, history, theme = 'dark' } = props;
  
  const formatSec = (s: number) => {
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    return rs > 0 ? `${m}m ${rs}s` : `${m}m`;
  };

  const isBetter = previousValue === null || currentValue > previousValue;

  return (
    <div className={`${theme === 'dark' ? 'bg-[#111] border-zinc-800 shadow-md' : 'bg-white border-gray-100 shadow-sm shadow-gray-200/50 hover:shadow-md'} border rounded-2xl p-5 hover:border-zinc-700 transition-all flex flex-col group`}>
      <div className="flex items-center justify-between mb-3">
        <div className="space-y-1">
          <p className={`${theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} text-[10px] font-black uppercase tracking-widest`}>{displayName}</p>
          <div className="flex items-baseline gap-2">
              <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'} text-3xl font-black italic tracking-tighter`}>{formatSec(currentValue)}</h3>
          </div>
        </div>
        <div className={`p-2 rounded-xl ${isBetter ? 'bg-[#AAD816]/10 text-[#AAD816] font-bold' : 'bg-gray-100 text-gray-400'}`}>
          <Timer size={18} />
        </div>
      </div>

      <HistoricalProgress history={history} improvementDirection={improvementDirection} theme={theme} unit={unit} />
    </div>
  );
};

// 5. Cardio (Time-Based)
export const CardioMetricCard: React.FC<MetricProps> = ({ 
  displayName, currentValue, previousValue, unit, improvementDirection, history, theme = 'dark' 
}) => {
  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const rs = s % 60;
    return `${m}:${rs.toString().padStart(2, '0')}`;
  };

  const isBetter = previousValue === null || currentValue < previousValue; 
  const diffSec = previousValue ? Math.abs(currentValue - previousValue) : null;

  return (
    <div className={`${theme === 'dark' ? 'bg-[#111] border-zinc-800' : 'bg-white border-gray-100 shadow-sm shadow-gray-200/50 hover:shadow-md'} border rounded-2xl p-5 hover:border-zinc-700 transition-all group shadow-sm`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <p className={`${theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} text-[10px] font-black uppercase tracking-widest`}>{displayName}</p>
          <div className="flex items-baseline gap-2 mt-1">
            <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'} text-3xl font-black italic tracking-tighter`}>{formatTime(currentValue)}</h3>
            {diffSec !== null && (
                <div className={`flex items-center gap-0.5 text-[11px] font-black uppercase ${isBetter ? 'text-[#AAD816]' : theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'}`}>
                    {isBetter ? '↓' : '↑'} {diffSec}s
                </div>
            )}
          </div>
        </div>
      </div>

      <HistoricalProgress history={history} improvementDirection={improvementDirection} theme={theme} unit={unit} />
    </div>
  );
};

// 6. Flexibility (Range Mobility)
export const FlexibilityMetricCard: React.FC<MetricProps> = (props) => {
  const { displayName, currentValue, unit, improvementDirection, history, theme = 'dark' } = props;

  return (
    <div className={`${theme === 'dark' ? 'bg-[#111] border-zinc-800' : 'bg-white border-gray-100 shadow-sm shadow-gray-200/50 hover:shadow-md'} border rounded-2xl p-5 hover:border-zinc-700 transition-all group shadow-sm`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <p className={`${theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} text-[10px] font-black uppercase tracking-widest`}>{displayName}</p>
          <div className="flex items-baseline gap-2 mt-1">
            <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'} text-3xl font-black italic tracking-tighter`}>{currentValue}</h3>
            <span className={`text-xs ${theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} font-bold uppercase italic`}>{unit}</span>
          </div>
        </div>
        <Ruler size={18} className={`${theme === 'dark' ? 'text-zinc-600' : 'text-gray-300'} group-hover:text-[#AAD816]/50 transition-colors`} />
      </div>

      <HistoricalProgress history={history} improvementDirection={improvementDirection} theme={theme} unit={unit} />
    </div>
  );
};

// 7. Performance Tests
export const PerformanceMetricCard: React.FC<MetricProps> = (props) => {
  const { displayName, currentValue, unit, improvementDirection, history, theme = 'dark' } = props;
  return (
    <div className={`${theme === 'dark' ? 'bg-[#111] border-zinc-800 shadow-md' : 'bg-white border-gray-100 shadow-sm shadow-gray-200/50 hover:shadow-md'} border rounded-2xl p-5 hover:border-zinc-700 transition-all group relative overflow-hidden`}>
       <div className="absolute top-0 right-0 w-24 h-24 bg-[#AAD816]/5 blur-3xl rounded-full translate-x-12 -translate-y-12" />
       
       <div className="flex justify-between items-start mb-1 relative z-10">
        <p className={`${theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} text-[10px] font-black uppercase tracking-widest leading-none`}>{displayName}</p>
        <Target size={18} className="text-[#AAD816]/20 group-hover:text-[#AAD816]/40 transition-colors" />
      </div>

      <div className="flex items-baseline gap-2 mb-2 relative z-10">
        <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'} text-4xl font-black italic tracking-tighter leading-none`}>{currentValue}</h3>
        <span className={`text-xs ${theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} not-italic font-black uppercase`}>{unit}</span>
      </div>

      <HistoricalProgress history={history} improvementDirection={improvementDirection} theme={theme} unit={unit} />
    </div>
  );
};

// --- Main Dynamic Renderer ---

export const MetricRenderer: React.FC<{
  displayType: string;
  metricData: MetricProps;
  theme?: 'light' | 'dark';
}> = ({ displayType, metricData, theme = 'dark' }) => {
  const props = { ...metricData, theme };
  switch (displayType) {
    case 'body': return <BodyMetricCard {...props} />;
    case 'strength': return <StrengthMetricCard {...props} />;
    case 'reps': return <RepMetricCard {...props} />;
    case 'duration': return <DurationMetricCard {...props} />;
    case 'cardio': return <CardioMetricCard {...props} />;
    case 'flexibility': return <FlexibilityMetricCard {...props} />;
    case 'performance': return <PerformanceMetricCard {...props} />;
    default: return <BodyMetricCard {...props} />;
  }
};
